import React, { useCallback, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { PALETTE, axisProps, tooltipStyle } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Who the hospital serves: the age and gender make-up of patients, either those actually seen in
// the range (default) or everyone on the register.

const BUCKET_ORDER = ['0-4', '5-12', '13-17', '18-29', '30-44', '45-59', '60+', 'Unknown'];
const GENDERS = [
  { key: 'MALE', label: 'Male', color: PALETTE[4] },
  { key: 'FEMALE', label: 'Female', color: PALETTE[6] },
  { key: 'OTHER', label: 'Other', color: PALETTE[2] },
  { key: 'UNKNOWN', label: 'Not recorded', color: PALETTE[5] },
];

const AgeGenderReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisYear'));
  const [preset, setPreset] = useState('thisYear');
  const [scope, setScope] = useState('visited');

  const fetcher = useCallback(
    () => reportsAPI.getAgeGender({ from: range.from, to: range.to, scope }),
    [range, scope]
  );
  const { data, loading } = useReport(fetcher);

  // rows [{ageBucket, gender, patients}] -> pivot per bucket for the grouped chart and crosstab.
  const pivot = useMemo(() => {
    const byBucket = {};
    (data?.rows || []).forEach((r) => {
      const b = (byBucket[r.ageBucket] = byBucket[r.ageBucket] || { ageBucket: r.ageBucket, total: 0 });
      b[r.gender] = (b[r.gender] || 0) + Number(r.patients);
      b.total += Number(r.patients);
    });
    return BUCKET_ORDER.filter((b) => byBucket[b]).map((b) => byBucket[b]);
  }, [data]);

  const activeGenders = GENDERS.filter((g) => pivot.some((row) => row[g.key]));

  const columns = [
    { key: 'ageBucket', label: 'Age Group' },
    ...activeGenders.map((g) => ({ key: g.key, label: g.label, align: 'right', render: (r) => r[g.key] || 0, csv: (r) => r[g.key] || 0 })),
    { key: 'total', label: 'Total', align: 'right', render: (r) => <span className="font-semibold">{r.total}</span>, csv: (r) => r.total },
  ];

  return (
    <div className="space-y-5">
      {scope === 'visited' && (
        <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
      )}

      <div className="flex items-center gap-3">
        <div className="bg-white rounded-xl shadow p-1 inline-flex">
          <button
            onClick={() => setScope('visited')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              scope === 'visited' ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Patients seen in range
          </button>
          <button
            onClick={() => setScope('registered')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              scope === 'registered' ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            All registered patients
          </button>
        </div>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <ChartPanel title="Patients by age group and gender">
        {loading || pivot.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No patients match'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={pivot} margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
              <XAxis dataKey="ageBucket" {...axisProps} />
              <YAxis {...axisProps} allowDecimals={false} />
              <Tooltip {...tooltipStyle} />
              <Legend />
              {activeGenders.map((g) => (
                <Bar key={g.key} dataKey={g.key} name={g.label} fill={g.color} radius={[3, 3, 0, 0]} barSize={20} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'ageBand', value: r.ageBucket, scope, from: range.from, to: range.to })}
        title="Age and gender crosstab"
        columns={columns}
        rows={pivot}
        csvName={`age-gender-${scope}`}
        searchable={false}
      />

      <ReportPrintLayout
        title="Age & Gender Distribution"
        subtitle={
          scope === 'registered'
            ? 'Scope: all registered patients'
            : `Scope: patients seen ${range.from} to ${range.to}`
        }
        tables={[{ title: 'Age and gender crosstab', columns, rows: pivot }]}
      />

      {drill.node}
    </div>
  );
};

export default AgeGenderReport;
